import type { ReactNode } from "react"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Label from "@/v2-ui/label"
import Separator from "@/v2-ui/separator"
import cn from "@/v2-ui/utils/utils.cn"
import { DotText } from "@/v2-backoffice-client/components/dot"
import FeatureNotice from "@/v2-backoffice-client/feature/FeatureNotice"
import FeatureUnpublishedNotice from "@/v2-backoffice-client/feature/FeatureUnpublishedNotice"
import FeatureAccessSection from "@/v2-backoffice-client/feature/access/FeatureAccessSection"
import FeatureAccessSkeleton from "@/v2-backoffice-client/feature/access/FeatureAccessSkeleton"
import FeatureCountrySection from "@/v2-backoffice-client/feature/country/FeatureCountrySection"
import { COUNTRY_EMPTY_MEANING } from "@/v2-backoffice-client/feature/country/feature.country.constants"
import { useFeatureQuery } from "@/v2-backoffice-client/feature/feature.query"
import LayerEnableSwitch from "@/v2-backoffice-client/layer/enable/LayerEnableSwitch"
import LayerCategoryManager from "@/v2-backoffice-client/layer/view/LayerCategoryManager"
import {
  useLayerVisibilityGates,
  type LayerVisibilityGate,
} from "@/v2-backoffice-client/layer/visibility/layer.visibility.hooks"
import { LAYER_VISIBILITY_QUERY_NAME } from "@/v2-backoffice-client/layer/visibility/layer.visibility.constants"
import type { LayerFragment } from "@/v2-backoffice-client/layer/__types__/layer.fragment"

/** The visibility summary refetches its runtime facts after these saves. */
const VISIBILITY_REFETCH_QUERY_NAMES = [LAYER_VISIBILITY_QUERY_NAME]

export type LayerAvailabilityTabProps = {
  className?: string
  layer: LayerFragment
}

/**
 * Everything that decides whether a customer sees this layer, in the order
 * the console checks it: published, countries, availability, category
 * filing, search index. Each section's heading carries that gate's current
 * verdict (the same facts the info row's "Visible to customers" summarises),
 * so making a layer available is one tab, top to bottom, instead of a hunt
 * across Details and Availability - which is how a published, filed,
 * translated layer went unnoticed with no country (2026-09-08).
 *
 * Everything here writes the running environment the moment it saves and
 * mirrors to the catalog; the texts on the Details tab are the one thing on
 * the sheet that waits for a translation publish. Who has the layer (the
 * Access gate) is the exception the other way: read here, decided on
 * Licensing > Feature access > Content, with the lock's blast radius in view.
 */
function LayerAvailabilityTab(props: LayerAvailabilityTabProps) {
  const { layer, className } = props

  const { gates } = useLayerVisibilityGates({ layer })
  const gateByKey = new Map(gates.map((gate) => [gate.key, gate]))

  const {
    data,
    loading: isLoadingFeature,
    error: featureError,
  } = useFeatureQuery({
    variables: { featureCode: layer.FeatureCode },
  })
  const feature = data?.getFeature

  return (
    <div className={cn("flex flex-col gap-5 p-3", className)}>
      <FeatureNotice tone="info">
        Every save on this tab goes live in this environment immediately and is
        mirrored to the catalog — no publish needed. Texts (Details tab) are the
        exception: they ship with the next translation publish. Who has the
        layer is decided on Licensing → Feature access, not here.
      </FeatureNotice>

      <GateSection title="Live" gate={gateByKey.get("published")}>
        <div className="flex items-center gap-3">
          <LayerEnableSwitch layer={layer} id="layer-availability-published" />
          <Label
            htmlFor="layer-availability-published"
            className="cursor-pointer font-normal"
          >
            {layer.IsEnabled
              ? "Live — listed for customers (subject to the gates below)"
              : "Not live — hidden from every customer"}
          </Label>
        </div>
        <p className="text-xs text-muted-foreground">
          The same switch as Go live / Take offline in the toolbar: flipping it
          stamps you as the reviewer. Unrelated to publishing the catalog, which
          ships definitions and leaves this switch alone.
        </p>
      </GateSection>

      <Separator />

      <GateSection title="Countries" gate={gateByKey.get("countries")}>
        <FeatureCountrySection
          featureCode={layer.FeatureCode}
          subject="layer"
          featureCodeLabel="Layers"
          emptyMeans={COUNTRY_EMPTY_MEANING.nowhere}
          refetchQueryNames={VISIBILITY_REFETCH_QUERY_NAMES}
        />
      </GateSection>

      <Separator />

      <GateSection title="Access" gate={gateByKey.get("access")}>
        {isLoadingFeature ? (
          <FeatureAccessSkeleton />
        ) : feature ? (
          <FeatureAccessSection feature={feature} featureCodeLabel="Layers" />
        ) : featureError ? (
          // A failed read is not "unpublished": say what happened instead.
          <ErrorMessage error={featureError} />
        ) : (
          <FeatureUnpublishedNotice subject="layer" />
        )}
      </GateSection>

      <Separator />

      <GateSection title="Categories" gate={gateByKey.get("categories")}>
        <LayerCategoryManager
          isHeadingHidden
          layerFeatureCode={layer.FeatureCode}
          categoryCodes={layer.CategoryCodes}
        />
      </GateSection>

      <Separator />

      <GateSection title="Search index" gate={gateByKey.get("search")}>
        <p className="text-xs text-muted-foreground">
          Built from the layer&apos;s texts. A layer that is not in the index is
          still listed in the chooser, but its search box never finds it; the
          next catalog publish rebuilds the index.
        </p>
      </GateSection>
    </div>
  )
}

type GateSectionProps = {
  title: string
  /** Undefined while the gates are loading, or when the runtime has no row. */
  gate: LayerVisibilityGate | undefined
  children: ReactNode
}

/** One gate: its name, its current verdict, and the control that moves it. */
function GateSection(props: GateSectionProps) {
  const { title, gate, children } = props

  return (
    <section className="flex flex-col gap-2">
      <header className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="text-sm font-medium">{title}</span>
        {gate && (
          <DotText
            className="text-xs"
            variant={
              gate.isOk
                ? "success-1"
                : gate.isBlocking
                  ? "destructive"
                  : "warning"
            }
          >
            {gate.detail}
          </DotText>
        )}
      </header>
      {children}
    </section>
  )
}

export default LayerAvailabilityTab
